function ProgressSteps({ steps, currentStep }) {
  if (!steps?.length) return null;

  return (
    <ol className="flex items-center" aria-label="Account setup progress">
      {steps.map((step, index) => {
        const stepNumber = index + 1;
        const isComplete = stepNumber < currentStep;
        const isCurrent = stepNumber === currentStep;
        return (
          <li key={step} className="flex min-w-0 flex-1 items-center last:flex-none">
            <div className="flex min-w-0 flex-col items-center gap-2">
              <span
                className={`flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold transition ${
                  isComplete
                    ? "border-mint bg-mint text-white"
                    : isCurrent
                      ? "border-accent bg-accent text-white shadow-lg shadow-accent/20"
                      : "border-black/10 bg-white text-slate-400"
                }`}
                aria-current={isCurrent ? "step" : undefined}
              >
                {isComplete ? "✓" : stepNumber}
              </span>
              <span
                className={`hidden whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.16em] sm:block ${
                  isCurrent ? "text-accent" : "text-slate-400"
                }`}
              >
                {step}
              </span>
            </div>
            {index < steps.length - 1 && (
              <span
                className={`mx-3 h-px flex-1 ${isComplete ? "bg-mint" : "bg-black/10"}`}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

export default function AuthLayout({ title, subtitle, children, footer, steps, currentStep }) {
  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="pointer-events-none absolute -left-24 top-20 h-64 w-64 rounded-full bg-mint/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-10 h-72 w-72 rounded-full bg-brand/10 blur-3xl" />
      <div className="relative mx-auto grid min-h-screen max-w-6xl items-center gap-12 px-5 py-10 md:grid-cols-[1.05fr_0.95fr] md:px-8">
        <section className="space-y-6 fade-up">
          <ProgressSteps steps={steps} currentStep={currentStep} />
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-[0.4em] text-slate-500">Divine Cooperative</p>
            <h1 className="font-display text-4xl text-accent md:text-5xl">{title}</h1>
            <p className="max-w-xl text-sm leading-6 text-slate-600 md:text-base">{subtitle}</p>
          </div>
          <div className="card-surface rounded-[28px] p-6 md:p-8">{children}</div>
          {footer && <div className="text-xs text-slate-500">{footer}</div>}
        </section>
        <aside className="relative hidden min-h-[620px] overflow-hidden rounded-[40px] bg-accent p-10 text-white shadow-2xl shadow-accent/20 md:flex md:flex-col md:justify-between">
          <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full border border-white/10" />
          <div className="absolute -right-6 -top-6 h-32 w-32 rounded-full border border-white/10" />
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.35em] text-white/60">
              Built for members
            </p>
            <h2 className="mt-5 font-display text-4xl !text-white">Your cooperative, in one place.</h2>
            <p className="mt-4 max-w-sm text-sm leading-6 text-white/70">
              Build savings, access responsible finance, and follow every contribution with a clear
              digital record.
            </p>
            <div className="mt-10 space-y-4 text-sm">
              {["Secure account verification", "Transparent financial activity", "Member-first support"].map(
                (item) => (
                  <div key={item} className="flex items-center gap-3">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white/10 text-mint">
                      ✓
                    </span>
                    <span className="text-white/85">{item}</span>
                  </div>
                )
              )}
            </div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/10 p-6 text-xs leading-5 text-white/70 backdrop-blur">
            <p className="text-sm font-semibold text-white">Secure by design</p>
            <p className="mt-2">
              One-time verification codes and role-based access help keep every member account
              protected.
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}
